/** Create + edit + delete modals for a gradebook, built on the app's shared
 * Modal (same pattern as EditReportFolderModal / DeleteReportModal).
 *
 * Create and edit are the same form - only the copy, the icon and the submit
 * label differ - so both are thin wrappers over one `GradebookFormModal`. */

import { useState, useEffect } from "react";
import { LucidePencil, LucidePlus, LucideTrash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Modal, ModalBody, ModalFooter } from "@/components/modals/Modal";
import { TITLE_COLORS } from "@/gradebook/titleColors";
import { currentSchoolYear, schoolYearOptions } from "@/gradebook/schoolYear";

const GRADE_OPTIONS = ["K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

const nativeSelect =
  "h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm " +
  "ring-offset-background transition-colors focus:outline-none focus:ring-2 " +
  "focus:ring-ring focus:ring-offset-2 text-foreground";

const EMPTY = { subject: "", grade: "", schoolYear: "", titleColor: "default" };

/**
 * The shared gradebook form. `gradebook` null-vs-object is what the callers use
 * as "closed vs open", so create passes a blank object rather than null.
 */
function GradebookFormModal({
  open, initial, title, subtitle, icon, submitLabel, onClose, onSubmit,
}) {
  const [form, setForm] = useState(EMPTY);

  // Reseed whenever the modal opens, so a cancelled edit never leaks into the
  // next one and create always starts clean.
  useEffect(() => {
    if (!open) return;
    setForm({
      subject: initial?.subject || "",
      grade: initial?.grade || "",
      schoolYear: initial?.schoolYear || currentSchoolYear(),
      titleColor: initial?.titleColor || "default",
    });
  }, [open, initial]);

  const canSave = form.subject.trim() && form.grade && form.schoolYear;

  function handleSubmit(e) {
    e.preventDefault();
    if (!canSave) return;
    onSubmit({
      subject: form.subject.trim(),
      grade: form.grade,
      schoolYear: form.schoolYear,
      titleColor: form.titleColor,
    });
  }

  /* Offer the three plausible years, plus whatever this gradebook already has -
   * an older book must not have its year silently rewritten by being edited. */
  const years = schoolYearOptions();
  const yearChoices = form.schoolYear && !years.includes(form.schoolYear)
    ? [form.schoolYear, ...years]
    : years;

  return (
    <Modal open={open} onClose={onClose} title={title} subtitle={subtitle} icon={icon}>
      <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
        <ModalBody className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Subject name</Label>
            <Input
              value={form.subject}
              onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
              placeholder="Science and Technology"
              className="h-10"
              required
            />
          </div>

          <div className="flex gap-3">
            <div className="space-y-1.5" style={{ width: 110 }}>
              <Label className="text-sm font-medium">Grade</Label>
              <select
                value={form.grade}
                onChange={(e) => setForm((p) => ({ ...p, grade: e.target.value }))}
                className={nativeSelect}
                required
              >
                <option value="">-</option>
                {GRADE_OPTIONS.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5 flex-1">
              <Label className="text-sm font-medium">School year</Label>
              <select
                value={form.schoolYear}
                onChange={(e) => setForm((p) => ({ ...p, schoolYear: e.target.value }))}
                className={nativeSelect}
                required
              >
                {yearChoices.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Subject title colour</Label>
            <div className="flex flex-wrap gap-2.5">
              {TITLE_COLORS.map((c) => {
                const selected = form.titleColor === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, titleColor: c.key }))}
                    title={c.label}
                    aria-label={c.label}
                    aria-pressed={selected}
                    className={`h-8 w-8 rounded-full ${c.swatch} ring-offset-2 ring-offset-card transition ${
                      selected
                        ? "ring-2 ring-primary"
                        : "ring-1 ring-border hover:ring-neutral-400"
                    }`}
                  />
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              Only the subject text changes colour - the book itself stays green.
            </p>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button type="submit" disabled={!canSave} className="flex-1">
            {submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

/**
 * Create a new gradebook. It starts empty - no students, no assessments - with
 * the default weight categories seeded by the API.
 *
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {(values: { subject, grade, schoolYear, titleColor }) => void} props.onCreate
 */
export function CreateGradebookModal({ open, onClose, onCreate }) {
  return (
    <GradebookFormModal
      open={open}
      initial={null}
      title="New Gradebook"
      subtitle="Name the subject and pick its grade - you can add students next."
      icon={<LucidePlus size={16} className="text-primary" />}
      submitLabel="Create Gradebook"
      onClose={onClose}
      onSubmit={onCreate}
    />
  );
}

/**
 * Edit a gradebook's subject name, grade, school year, and title colour.
 *
 * @param {object|null} props.gradebook - the gradebook being edited, or null when closed
 * @param {() => void}  props.onClose
 * @param {(gradebook: object, changes: object) => void} props.onSave
 */
export function EditGradebookModal({ gradebook, onClose, onSave }) {
  return (
    <GradebookFormModal
      open={!!gradebook}
      initial={gradebook}
      title="Edit Gradebook"
      subtitle="Rename the subject, change the grade or year, or pick a title colour."
      icon={<LucidePencil size={16} className="text-primary" />}
      submitLabel="Save Changes"
      onClose={onClose}
      onSubmit={(changes) => onSave(gradebook, changes)}
    />
  );
}

/**
 * Confirm deletion of a gradebook.
 *
 * @param {object|null} props.gradebook - the gradebook to delete, or null when closed
 * @param {() => void}  props.onClose
 * @param {() => void}  props.onConfirm
 */
export function DeleteGradebookModal({ gradebook, onClose, onConfirm }) {
  const [confirmText, setConfirmText] = useState("");

  // Reset the typed confirmation whenever a different gradebook opens the modal.
  useEffect(() => {
    if (gradebook) setConfirmText("");
  }, [gradebook]);

  const target = (gradebook?.subject ?? "").trim();
  const matches = target.length > 0 && confirmText.trim() === target;

  function handleConfirm() {
    if (matches) onConfirm();
  }

  return (
    <Modal
      open={!!gradebook}
      onClose={onClose}
      title="Delete Gradebook"
      subtitle="This action cannot be undone."
      icon={<LucideTrash2 size={16} className="text-destructive" />}
      maxWidth="max-w-sm"
    >
      <div className="px-6 py-5 space-y-4">
        <p className="text-sm text-muted-foreground">
          Are you sure you want to delete this gradebook? All of its students,
          assessments, and marks will be permanently removed.
        </p>
        <div className="rounded-lg border border-border bg-muted/40 px-4 py-3">
          <p className="text-sm font-semibold text-foreground">{gradebook?.subject}</p>
          <p className="text-xs text-muted-foreground">
            Grade {gradebook?.grade} · {gradebook?.schoolYear}
          </p>
        </div>

        {/* Type-to-confirm - guards against an accidental click on the red button. */}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">
            Type{" "}
            <span className="font-semibold text-foreground">{target}</span>{" "}
            to confirm
          </Label>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleConfirm();
            }}
            placeholder={target}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            className="h-10"
          />
        </div>

        <div className="flex gap-2 pt-1">
          <Button variant="destructive" onClick={handleConfirm} disabled={!matches}>
            Delete gradebook
          </Button>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Delete a student - type-to-confirm on the student's name.
 *
 * A student row is the one delete in the Gradebook with no undo path: the FK
 * cascade takes every mark they have in this book with them, and unlike a
 * gradebook or an assessment (both soft-deleted) there is nothing left to
 * restore. Hence the same type-the-name gate the gradebook delete uses, and an
 * explicit count of what goes with them.
 *
 * This is NOT the "student left mid-year" case - that one just stops getting
 * marks and keeps its row. This is for a row added in error.
 *
 * @param {object|null} props.student   - the student to delete, or null when closed
 * @param {number}      props.markCount - how many marks disappear with them
 * @param {() => void}  props.onClose
 * @param {() => void}  props.onConfirm
 */
export function DeleteStudentModal({ student, markCount = 0, onClose, onConfirm }) {
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (student) setConfirmText("");
  }, [student]);

  const target = (student?.name ?? "").trim();
  const matches = target.length > 0 && confirmText.trim() === target;

  function handleConfirm() {
    if (matches) onConfirm();
  }

  return (
    <Modal
      open={!!student}
      onClose={onClose}
      title="Delete Student"
      subtitle="This action cannot be undone."
      icon={<LucideTrash2 size={16} className="text-destructive" />}
      maxWidth="max-w-sm"
    >
      <div className="px-6 py-5 space-y-4">
        <p className="text-sm text-muted-foreground">
          Removing this student deletes their row and every mark recorded against
          them in this gradebook.
        </p>
        <div className="rounded-lg border border-border bg-muted/40 px-4 py-3">
          <p className="text-sm font-semibold text-foreground">{student?.name}</p>
          <p className="text-xs text-muted-foreground">
            {student?.oen ? `OEN ${student.oen} · ` : ""}
            {markCount === 1 ? "1 mark" : `${markCount} marks`} will be deleted
          </p>
        </div>

        {/* Type-to-confirm - guards against an accidental click on the red button. */}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">
            Type{" "}
            <span className="font-semibold text-foreground">{target}</span>{" "}
            to confirm
          </Label>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleConfirm();
            }}
            placeholder={target}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            className="h-10"
          />
        </div>

        <div className="flex gap-2 pt-1">
          <Button variant="destructive" onClick={handleConfirm} disabled={!matches}>
            Delete student
          </Button>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
